/**
 * A visible crash surface.
 *
 * The Mini App runs inside Telegram's webview, where there is no console to
 * open and no devtools to attach. Without this, any uncaught error is a white
 * screen carrying zero information — which is exactly how the first deployment
 * failed. An error the owner can read and forward is worth the ~40 lines.
 *
 * This is a development aid, not error reporting: Sentry (CLAUDE.md >
 * Operations) is what will carry these off the device later.
 */

function describe(err: unknown): string {
  if (err instanceof Error) {
    return `${err.name}: ${err.message}\n\n${err.stack ?? '(no stack)'}`
  }
  try {
    return JSON.stringify(err, null, 2)
  } catch {
    return String(err)
  }
}

export function showFatal(err: unknown): void {
  const root = document.getElementById('root')
  if (!root) return

  const details = describe(err)

  root.innerHTML = ''

  const wrap = document.createElement('div')
  wrap.style.cssText =
    'padding:16px;font:13px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;' +
    'color:var(--text,#111);background:var(--bg,#fff);min-height:100vh'

  const title = document.createElement('p')
  title.textContent = 'Приложение упало'
  title.style.cssText = 'font-weight:700;font-size:17px;margin:0 0 8px'

  const hint = document.createElement('p')
  hint.textContent = 'Покажи этот текст — по нему видно, где именно.'
  hint.style.cssText = 'margin:0 0 12px;opacity:.7'

  const pre = document.createElement('pre')
  pre.textContent = details
  pre.style.cssText =
    'white-space:pre-wrap;word-break:break-word;margin:0;padding:12px;' +
    'border-radius:12px;background:rgba(127,127,127,.14)'

  wrap.append(title, hint, pre)
  root.append(wrap)
}

export function installCrashReporter(): void {
  window.addEventListener('error', (event) => {
    showFatal(event.error ?? event.message)
  })

  window.addEventListener('unhandledrejection', (event) => {
    showFatal(event.reason)
  })
}
